import { useRef } from 'react';

import { isEmbeddedSidebar } from '@/lib/embedded-sidebar';
import { cn } from '@/lib/utils';

import { Skeleton } from '@/components/ui/skeleton';

import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

import ChatFooter from './Footer';

const NO_FILES = { max_size_mb: 0, max_files: 0, accept: {} };
const noop = () => undefined;

interface ThreadSkeletonProps {
    showComposer?: boolean;
    maxWidth?: string;
}

export default function ThreadSkeleton({
    showComposer = true,
    maxWidth
}: ThreadSkeletonProps) {
    const defaultMaxWidth = useLayoutMaxWidth();
    const layoutMaxWidth = maxWidth ?? defaultMaxWidth;
    const autoScrollRef = useRef(true);

    return (
        <div
            className="relative flex flex-col w-full h-full"
            aria-busy="true"
            aria-label="Loading conversation"
        >
            <div className="flex-1 min-h-0 overflow-hidden">
                <div
                    className="flex flex-col gap-10 mx-auto p-4 w-full"
                    style={{ maxWidth: layoutMaxWidth }}
                >
                    <div className="flex justify-end">
                        <Skeleton className="rounded-2xl w-[55%] h-16" />
                    </div>
                    <div className="flex flex-col gap-3">
                        <Skeleton className="w-1/3 h-4" />
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-11/12 h-4" />
                        <Skeleton className="w-5/6 h-4" />
                        <Skeleton className="w-2/3 h-4" />
                    </div>
                </div>
            </div>
            {showComposer && (
                <div
                    className={cn(
                        'flex flex-col mx-auto w-full',
                        !isEmbeddedSidebar && 'px-4'
                    )}
                    style={{
                        maxWidth: isEmbeddedSidebar
                            ? layoutMaxWidth
                            : `calc(${layoutMaxWidth} + 2rem)`
                    }}
                >
                    <ChatFooter
                        fileSpec={NO_FILES}
                        onFileUpload={noop}
                        onFileUploadError={noop}
                        autoScrollRef={autoScrollRef}
                        showIfEmptyThread
                        composerDisabled
                    />
                </div>
            )}
        </div>
    );
}
